/**
 * Return-visits browser recorder — state/transport half of T008-FE
 * (docs/IMPLEMENTATION_PLAN_RETURN_VISITS.md). React wiring (route/visibility
 * triggers, cleanup) is a separate component; this module owns the guard
 * order, the origin-wide lock, the fresh-config fetch, the local reservation
 * and the single bounded POST.
 *
 * Guard order (plan, "Browser/ingress protocol and delivery semantics"):
 *   build flag -> host -> eligible path -> visible document -> read
 *   preference -> exclusive Web Lock `buzzmap-return-visits` -> reread
 *   preference -> FETCH FRESH CONFIG (inside the lock) -> pure transition ->
 *   reserve observation -> POST -> conditional cohortAccepted update.
 *
 * Delivery is deliberately conservative: the local record is reserved BEFORE
 * the POST and an ambiguous outcome (timeout, network error, non-2xx) is
 * never resent. cohortAccepted flips only on a 2xx `new` response while the
 * exact reserved record is still present, still opted-in and still current.
 * No retry queue, no beacon, no nonce/identifier; no storage or no Web Locks
 * means no contribution and no fingerprint fallback.
 *
 * Every storage/network/lock access happens at call time inside try/catch —
 * nothing touches window/localStorage/document at module scope, and only the
 * two return-visits keys are ever read or written (consent/auth/PostHog keys
 * stay untouched).
 */
import {
  RETURN_VISITS_LOCK_NAME,
  RETURN_VISITS_OBSERVATION_KEY,
  RETURN_VISITS_OPTOUT_KEY,
  decodeReturnObservation,
  decodeReturnVisitsConfig,
  romeDay,
  transitionReturnObservation,
} from '@buzzmap/shared';
import type { ReturnContribution, ReturnObservation, ReturnVisitsConfig } from '@buzzmap/shared';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** The only production origin allowed to contribute (R3). */
const PRODUCTION_HOST = 'buzzeventmap.com';
/** Public paths excluded from measurement, together with their trailing-slash variants. */
const EXCLUDED_PATHS = new Set(['/stats', '/privacy', '/termini', '/cookie-policy']);
/** Opt-out marker payload; presence of the key is what readPreference checks. */
const OPTOUT_MARKER = '1';
const ENDPOINT = '/api/return-visits';
const CONFIG_QUERY = '?mode=config';
/** Bounded config GET timeout; a hung request must not hold the origin-wide lock forever. */
const CONFIG_TIMEOUT_MS = 5000;
/** Bounded POST timeout (keepalive send; outcome may be ambiguous — never resent). */
const POST_TIMEOUT_MS = 8000;

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface ReturnVisitPreference {
  optedOut: boolean;
  persistence: 'ok' | 'unavailable';
}

export interface ReturnVisitPreferenceResult {
  optedOut: boolean;
  persisted: boolean;
}

/** Minimal Storage surface actually used; every call is wrapped by the caller. */
export interface ReturnVisitStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface ReturnVisitsResponse {
  readonly ok: boolean;
  readonly status: number;
  json(): Promise<unknown>;
}

export type ReturnVisitsFetch = (
  input: string,
  init?: RequestInit
) => Promise<ReturnVisitsResponse>;

/** Structural subset of the Web Locks API actually used (method syntax: bivariant). */
export interface ReturnVisitsLocks {
  request(
    name: string,
    options: { mode?: 'exclusive' | 'shared' },
    callback: (lock: unknown) => Promise<unknown>
  ): Promise<unknown>;
}

export interface ReturnVisitRecorderOptions {
  /** Clock: the Rome day comes from `romeDay(now())`. */
  now?: () => Date;
  /** Storage provider; null (or a throwing provider) means "unavailable" = no contribution. */
  getStorage?: () => ReturnVisitStorage | null;
  fetchImpl?: ReturnVisitsFetch;
  /** Web Locks provider; null means "no locks" = no contribution. */
  getLocks?: () => ReturnVisitsLocks | null;
  /** Build gate: VITE_RETURN_VISITS_ENABLED === 'true' (default off, R7). */
  isEnabled?: () => boolean;
  isDev?: () => boolean;
  /** DEV-only escape hatch for localhost testing (with isDev). */
  isLocalTest?: () => boolean;
  getHostname?: () => string;
  getPathname?: () => string;
  getVisibilityState?: () => string;
  configTimeoutMs?: number;
  postTimeoutMs?: number;
}

export interface ReturnVisitRecorder {
  /** One guarded observation attempt (see guard order above). Never rejects. */
  record(): Promise<void>;
  readPreference(): ReturnVisitPreference;
  setPreference(optOut: boolean): ReturnVisitPreferenceResult;
  /** Invalidate the current generation and abort in-flight requests (unmount/objection). */
  cancel(): void;
  /** Cross-tab propagation: notifies on our own storage keys and syncs suppression from the marker. */
  subscribe(handler?: () => void): () => void;
}

// ---------------------------------------------------------------------------
// Defaults (all lazy: no window/document/storage access at module scope)
// ---------------------------------------------------------------------------

const defaultFetch: ReturnVisitsFetch = (input, init) => fetch(input, init);

function getDefaultStorage(): ReturnVisitStorage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function getDefaultLocks(): ReturnVisitsLocks | null {
  try {
    if (typeof navigator === 'undefined') return null;
    const locks = navigator.locks; // happy-dom returns null here; a browser without Web Locks also means no contribution
    return locks ? (locks as unknown as ReturnVisitsLocks) : null;
  } catch {
    return null;
  }
}

function getDefaultHostname(): string {
  if (typeof window === 'undefined') return '';
  return window.location.hostname;
}

function getDefaultPathname(): string {
  if (typeof window === 'undefined') return '';
  return window.location.pathname;
}

function getDefaultVisibilityState(): string {
  if (typeof document === 'undefined') return 'visible';
  return document.visibilityState;
}

/** Shared public-host gate for browser measures that reuse return-visit policy. */
export function isReturnVisitHostEligible(
  hostname: string,
  isDev = Boolean(import.meta.env.DEV),
  isLocalTest = import.meta.env.VITE_RETURN_VISITS_LOCAL_TEST === 'true'
): boolean {
  if (hostname === PRODUCTION_HOST) return true;
  return isDev && isLocalTest;
}

// ---------------------------------------------------------------------------
// Pure path eligibility (exported: the component scopes its triggers with it)
// ---------------------------------------------------------------------------

/**
 * True when `pathname` is an eligible public route: everything except
 * /stats, /privacy, /termini, /cookie-policy and their trailing-slash
 * variants. Pure — the component must pass the REAL current path on every
 * trigger (an SPA route change can cross the boundary on the same day).
 */
export function isEligibleReturnVisitPath(pathname: string): boolean {
  if (typeof pathname !== 'string') return false;
  const path = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return !EXCLUDED_PATHS.has(path);
}

// ---------------------------------------------------------------------------
// Recorder factory (internal seam: tests inject clock/storage/fetch/locks)
// ---------------------------------------------------------------------------

type StoredRead = { ok: true; value: string | null } | { ok: false };

/**
 * Creates an isolated recorder instance with its own suppression flag,
 * generation counter and call queue. Production code uses the module-level
 * wrappers over one default instance; tests use this to inject fakes.
 */
export function createReturnVisitRecorder(
  options: ReturnVisitRecorderOptions = {}
): ReturnVisitRecorder {
  const now = options.now ?? (() => new Date());
  const getStorage = options.getStorage ?? getDefaultStorage;
  const fetchImpl = options.fetchImpl ?? defaultFetch;
  const getLocks = options.getLocks ?? getDefaultLocks;
  const isEnabled =
    options.isEnabled ?? (() => import.meta.env.VITE_RETURN_VISITS_ENABLED === 'true');
  const isDev = options.isDev ?? (() => Boolean(import.meta.env.DEV));
  const isLocalTest =
    options.isLocalTest ?? (() => import.meta.env.VITE_RETURN_VISITS_LOCAL_TEST === 'true');
  const getHostname = options.getHostname ?? getDefaultHostname;
  const getPathname = options.getPathname ?? getDefaultPathname;
  const getVisibilityState = options.getVisibilityState ?? getDefaultVisibilityState;
  const configTimeoutMs = options.configTimeoutMs ?? CONFIG_TIMEOUT_MS;
  const postTimeoutMs = options.postTimeoutMs ?? POST_TIMEOUT_MS;

  /** In-memory objection (set by setPreference(true)); only storage sync clears it. */
  let suppression = false;
  /** Bumped by cancel(): every in-flight step compares against it before acting. */
  let generation = 0;
  /** Serializes record() calls inside this tab (StrictMode double effect). */
  let queue: Promise<void> = Promise.resolve();
  const abortControllers = new Set<AbortController>();

  // --- guarded storage primitives -----------------------------------------

  function storageGet(key: string): StoredRead {
    try {
      const storage = getStorage();
      if (storage === null) return { ok: false };
      return { ok: true, value: storage.getItem(key) };
    } catch {
      return { ok: false };
    }
  }

  function storageSet(key: string, value: string): boolean {
    try {
      const storage = getStorage();
      if (storage === null) return false;
      storage.setItem(key, value);
      return true;
    } catch {
      return false;
    }
  }

  function storageRemove(key: string): boolean {
    try {
      const storage = getStorage();
      if (storage === null) return false;
      storage.removeItem(key);
      return true;
    } catch {
      return false;
    }
  }

  /** Write + readback: true only when the exact value is readable afterwards. */
  function writeConfirmed(key: string, value: string): boolean {
    if (!storageSet(key, value)) return false;
    const readback = storageGet(key);
    return readback.ok && readback.value === value;
  }

  /** Remove + readback: true only when the key is confirmed gone. */
  function removeConfirmed(key: string): boolean {
    if (!storageRemove(key)) return false;
    const readback = storageGet(key);
    return readback.ok && readback.value === null;
  }

  // --- preference ----------------------------------------------------------

  function readPreference(): ReturnVisitPreference {
    const marker = storageGet(RETURN_VISITS_OPTOUT_KEY);
    if (!marker.ok) return { optedOut: suppression, persistence: 'unavailable' };
    return { optedOut: suppression || marker.value !== null, persistence: 'ok' };
  }

  /** The contribution gate: durable preference readable AND not opted out. */
  function preferenceAllowsContribution(): boolean {
    const preference = readPreference();
    return preference.persistence === 'ok' && !preference.optedOut;
  }

  function applyOptOut(): ReturnVisitPreferenceResult {
    suppression = true;
    cancel(); // objection stops current-tab work immediately, without waiting for the network lock
    const markerPersisted = writeConfirmed(RETURN_VISITS_OPTOUT_KEY, OPTOUT_MARKER);
    const observationRemoved = removeConfirmed(RETURN_VISITS_OBSERVATION_KEY);
    return { optedOut: true, persisted: markerPersisted && observationRemoved };
  }

  function applyOptIn(): ReturnVisitPreferenceResult {
    // Re-enable only after confirmed durable marker removal; otherwise the
    // marker (if any) still stands and the tab remains off.
    const removed = removeConfirmed(RETURN_VISITS_OPTOUT_KEY);
    if (removed) suppression = false;
    return { optedOut: readPreference().optedOut, persisted: removed };
  }

  function setPreference(optOut: boolean): ReturnVisitPreferenceResult {
    return optOut ? applyOptOut() : applyOptIn();
  }

  // --- cancellation --------------------------------------------------------

  function cancel(): void {
    generation += 1;
    for (const controller of abortControllers) {
      try {
        controller.abort();
      } catch {
        // an already-aborted controller must not break the loop
      }
    }
    abortControllers.clear();
  }

  /** Registers a fresh AbortController so cancel() can abort this request too. */
  function trackController(): { controller: AbortController; done: () => void } {
    const controller = new AbortController();
    abortControllers.add(controller);
    return {
      controller,
      done: () => {
        abortControllers.delete(controller);
      },
    };
  }

  // --- guards --------------------------------------------------------------

  function isHostEligible(): boolean {
    return isReturnVisitHostEligible(getHostname(), isDev(), isLocalTest());
  }

  function isDocumentConcealed(): boolean {
    const state = getVisibilityState();
    return state === 'hidden' || state === 'prerender';
  }

  function guardsPass(): boolean {
    if (!isEnabled()) return false;
    if (!isHostEligible()) return false;
    if (!isEligibleReturnVisitPath(getPathname())) return false;
    if (isDocumentConcealed()) return false;
    return true;
  }

  // --- network -------------------------------------------------------------

  /**
   * Abort-after-timeout for ONE network call: the signal goes on the request,
   * and the caller's `finally` must call `finish()` to clear the timer and
   * unregister the controller from cancel().
   */
  function beginBoundedRequest(timeoutMs: number): { signal: AbortSignal; finish: () => void } {
    const tracked = trackController();
    const timer = setTimeout(() => tracked.controller.abort(), timeoutMs);
    return {
      signal: tracked.controller.signal,
      finish: () => {
        clearTimeout(timer);
        tracked.done();
      },
    };
  }

  async function fetchConfig(): Promise<ReturnVisitsConfig | null> {
    const request = beginBoundedRequest(configTimeoutMs);
    try {
      const res = await fetchImpl(ENDPOINT + CONFIG_QUERY, {
        method: 'GET',
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
        cache: 'no-store',
        signal: request.signal,
      });
      if (!res.ok) return null;
      return decodeReturnVisitsConfig(await res.json());
    } catch {
      return null; // network/timeout/abort: no contribution, never rethrown
    } finally {
      request.finish();
    }
  }

  async function postContribution(contribution: ReturnContribution): Promise<boolean> {
    const request = beginBoundedRequest(postTimeoutMs);
    try {
      const res = await fetchImpl(ENDPOINT, {
        method: 'POST',
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
        cache: 'no-store',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contribution),
        signal: request.signal,
      });
      return res.ok; // 2xx only (T006 success is 204)
    } catch {
      return false; // ambiguous outcome: never resent, no acceptance update
    } finally {
      request.finish();
    }
  }

  // --- local record --------------------------------------------------------

  type StoredObservation = { ok: true; observation: ReturnObservation | null } | { ok: false };

  function readStoredObservation(): StoredObservation {
    const raw = storageGet(RETURN_VISITS_OBSERVATION_KEY);
    if (!raw.ok) return { ok: false };
    if (raw.value === null) return { ok: true, observation: null };
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw.value);
    } catch {
      return { ok: false }; // corrupt payload: fail closed, never overwrite/reenroll
    }
    const observation = decodeReturnObservation(parsed);
    if (observation === null) return { ok: false }; // present but invalid: same fail-closed rule
    return { ok: true, observation };
  }

  /** Reserve = durable write + readback of the exact serialized record, before any POST. */
  function reserveObservation(observation: ReturnObservation): string | null {
    const reservedRaw = JSON.stringify(observation);
    if (!writeConfirmed(RETURN_VISITS_OBSERVATION_KEY, reservedRaw)) return null;
    return reservedRaw;
  }

  /**
   * The ONLY post-success write: flip cohortAccepted on a `new` contribution,
   * and only while the exact reserved record still exists and the preference
   * is still a non-opted-out 'ok' — a late response must never recreate a
   * record deleted by an objection.
   */
  function confirmEnrollment(reserved: ReturnObservation, reservedRaw: string): void {
    if (!preferenceAllowsContribution()) return;
    const current = storageGet(RETURN_VISITS_OBSERVATION_KEY);
    if (!current.ok || current.value !== reservedRaw) return;
    writeConfirmed(
      RETURN_VISITS_OBSERVATION_KEY,
      JSON.stringify({ ...reserved, cohortAccepted: true })
    );
  }

  // --- the locked run ------------------------------------------------------

  async function runInsideLock(generationAtStart: number): Promise<void> {
    if (generationAtStart !== generation) return;
    if (!preferenceAllowsContribution()) return; // reread INSIDE the lock (another tab may have objected)
    const config = await fetchConfig(); // fresh config, inside the lock
    if (config === null || generationAtStart !== generation) return;
    // Re-read the preference AFTER the config await: a cross-tab objection
    // persisted while the fetch was in flight (marker + observation removal)
    // touches neither this generation nor this tab's suppression, and the
    // reservation below must never recreate the deleted record.
    if (!preferenceAllowsContribution()) return;
    const today = romeDay(now());
    // Client/server Rome day mismatch: the server would answer 409, so do not
    // reserve `last` for a day it will reject.
    if (config.today !== today) return;
    const stored = readStoredObservation();
    if (!stored.ok) return;
    const transition = transitionReturnObservation(stored.observation, config, today);
    if (transition.contribution === null || transition.observation === null) return;
    const reservedRaw = reserveObservation(transition.observation);
    if (reservedRaw === null || generationAtStart !== generation) return;
    const accepted = await postContribution(transition.contribution);
    if (!accepted || generationAtStart !== generation) return;
    if (transition.contribution.kind === 'new') {
      confirmEnrollment(transition.observation, reservedRaw);
    }
  }

  async function runOnce(): Promise<void> {
    const generationAtStart = generation;
    if (!guardsPass()) return;
    if (!preferenceAllowsContribution()) return;
    const locks = getLocks();
    if (locks === null) return; // no Web Locks: no contribution, no fallback
    try {
      await locks.request(RETURN_VISITS_LOCK_NAME, { mode: 'exclusive' }, async () => {
        await runInsideLock(generationAtStart);
      });
    } catch {
      // lock/protocol failure: skipped contribution, never rethrown
    }
  }

  function record(): Promise<void> {
    const run = queue.then(() => runOnce()).catch(() => undefined);
    queue = run;
    return run;
  }

  // --- cross-tab propagation ----------------------------------------------

  function subscribe(handler?: () => void): () => void {
    if (typeof window === 'undefined') return () => {};
    const onStorage = (event: Event): void => {
      const key = (event as StorageEvent).key;
      if (key === RETURN_VISITS_OPTOUT_KEY) {
        // The marker is the durable truth: sync local suppression from it, so a
        // successful objection (or re-enable) in one tab stops/releases the others.
        const marker = storageGet(RETURN_VISITS_OPTOUT_KEY);
        if (marker.ok) suppression = marker.value !== null;
      } else if (key !== RETURN_VISITS_OBSERVATION_KEY) {
        return; // consent/auth/PostHog keys are not ours to observe
      }
      handler?.();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }

  return { record, readPreference, setPreference, cancel, subscribe };
}

// ---------------------------------------------------------------------------
// Module-level default instance (the public interface for the component)
// ---------------------------------------------------------------------------

const defaultRecorder = createReturnVisitRecorder();

/** One guarded observation attempt. Never rejects; a skipped attempt resolves. */
export function recordReturnVisit(): Promise<void> {
  return defaultRecorder.record();
}

export function readReturnVisitPreference(): ReturnVisitPreference {
  return defaultRecorder.readPreference();
}

export function setReturnVisitPreference(optOut: boolean): ReturnVisitPreferenceResult {
  return defaultRecorder.setPreference(optOut);
}

/** Cancellation hook for unmount/objection: invalidates the generation and aborts in-flight work. */
export function cancelReturnVisitWork(): void {
  defaultRecorder.cancel();
}

/** Cross-tab propagation helper for the component (storage events on our two keys). */
export function subscribeReturnVisitPreferenceChanges(handler?: () => void): () => void {
  return defaultRecorder.subscribe(handler);
}
